import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Tooltip, Modal, Button, Slider, IconButton } from '@mui/material';
import { InfoOutlinedIcon, EditOutlinedIcon, CloseIcon, LockOutlinedIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'team/shared/utils/images/teamCropImageUtils';
import { useQRCodeContext } from 'contexts/QrCodeContext';
import { useParams } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';

const TeamQrCodeCustomLogo = ({ forceReset, isLogoRestricted = false, canRequestChange = false, onRestrictionNotification, cardId: propCardId }) => {
    const [image, setImage] = useState(null);
    const [open, setOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [objectUrl, setObjectUrl] = useState(null);
    const [inputDisabled, setInputDisabled] = useState(false);

    const { cardId: paramCardId } = useParams();
    const { authState } = useAuth();
    
    // ✅ TEMPLATE CONTEXT: Use prop cardId if provided, otherwise use authenticated user's card if no cardId from params
    const isTemplateContext = !paramCardId || window.location.pathname.includes('/template/');
    const userCardId = authState.user?.activeCardId || 
                      (authState.user?.contactCardIds?.length > 0 ? authState.user.contactCardIds[0] : null);
    const cardId = propCardId || (isTemplateContext ? userCardId : paramCardId);
    
    const { qrCodeState, setQRCodeLogoUrl } = useQRCodeContext();
    const currentLogoUrl = cardId ? qrCodeState[cardId]?.contextQrCodeLogoUrl : null;

    // Calculate initial zoom based on image dimensions
    const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
        const containerWidth = window.innerWidth * 0.9;
        const containerHeight = window.innerHeight * 0.9;
        const widthRatio = containerWidth / mediaWidth;
        const heightRatio = containerHeight / mediaHeight;
        return Math.min(widthRatio, heightRatio);
    }, []);

    const onMediaLoaded = useCallback((mediaSize) => {
        const initialZoom = calculateInitialZoom(mediaSize.width, mediaSize.height);
        setZoom(initialZoom);
        setCrop({ x: 0, y: 0 });
    }, [calculateInitialZoom]);

    const handleImageChange = (event) => {
        // Check if logo upload is restricted
        if (isLogoRestricted) {
            if (onRestrictionNotification) {
                const message = canRequestChange
                    ? 'QR Code custom logo is restricted by your subteam. You can submit a change request.'
                    : 'QR Code custom logo is restricted by your subteam and cannot be changed.';
                onRestrictionNotification(message);
            }
            event.target.value = '';
            return;
        }

        if (event.target.files && event.target.files[0]) {
            const file = URL.createObjectURL(event.target.files[0]);
            setObjectUrl(file);
            setImage(file);

            const img = new Image();
            img.onload = () => {
                const initialZoom = calculateInitialZoom(img.width, img.height);
                setZoom(initialZoom);
            };
            img.src = file;

            setOpen(true);
            event.target.value = '';
        }
    };

    const handleRemoveImage = () => {
        if (!cardId) return; // Don't proceed if no cardId
        
        // Check if logo removal is restricted
        if (isLogoRestricted) {
            if (onRestrictionNotification) {
                const message = canRequestChange
                    ? 'QR Code custom logo is restricted by your subteam. You can submit a change request.'
                    : 'QR Code custom logo is restricted by your subteam and cannot be removed.';
                onRestrictionNotification(message);
            }
            return;
        }

        setCroppedImage(null);
        setQRCodeLogoUrl(cardId, '');
        setImage(null);

        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            setObjectUrl(null);
        }

        setInputDisabled(true);
        setTimeout(() => setInputDisabled(false), 0);
    };

    const handleClose = () => {
        setOpen(false);
        setImage(null);
        setObjectUrl(null);
        setZoom(1);
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleSave = async () => {
        if (!cardId) return; // Don't proceed if no cardId
        
        try {
            const croppedImageBlob = await getCroppedImg(image, croppedAreaPixels, 'rectangle', `qr-logo-${cardId}`);
            const croppedImageUrl = URL.createObjectURL(croppedImageBlob);

            setCroppedImage(croppedImageUrl);
            setQRCodeLogoUrl(cardId, croppedImageUrl);
            setOpen(false);
        } catch (e) {
            console.error('Error cropping image:', e);
        }
    };

    // Reset when forceReset changes
    useEffect(() => {
        if (cardId) {
        handleRemoveImage();
        }
    }, [forceReset, cardId]);



    return (
        <Box sx={{ marginBottom: 2 }}>
            <Typography
                variant="body2"
                sx={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: '#4f4f4f',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '13px'
                }}
            >
                Custom Logo
                {isLogoRestricted && <LockOutlinedIcon sx={{ fontSize: '0.875rem', color: '#999', marginLeft: 0.5 }} />}
                <Tooltip title="Ideal Dimensions: 100 px by 100 px (1:1)" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: '1rem', marginLeft: 0.5 }} />
                </Tooltip>
            </Typography>


            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center', // Changed from flex-start to center for vertical alignment
                    marginBottom: '20px',
                    marginTop: '20px',
                    marginLeft: '5px', // Align to the right
                    gap: '16px', // Add consistent spacing between elements
                    maxWidth: '600px' // Control the overall width of the container
                }}>



                <Box sx={{ position: 'relative', width: 100, height: 100 }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '20%',
                            backgroundColor: '#f2f2f2',
                            border: '1px solid #dbdbdb',
                            cursor: isLogoRestricted ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            textAlign: 'center',
                            padding: 1,
                            backgroundImage: croppedImage || currentLogoUrl ? `url(${croppedImage || currentLogoUrl})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            opacity: isLogoRestricted ? 0.6 : 1,
                        }}
                        component={isLogoRestricted ? 'div' : 'label'}
                    >
                        {!croppedImage && !currentLogoUrl && (
                            <>
                                <EditOutlinedIcon sx={{ fontSize: '25px', color: '#828282', marginBottom: '5px' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 400, color: '#212121' }}>
                                    <span style={{ color: 'blue' }}>Select</span> file or drag and drop here
                                </Typography>
                            </>
                        )}

                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                top: 0,
                                left: 0,
                                opacity: 0,
                                cursor: isLogoRestricted ? 'not-allowed' : 'pointer',
                                pointerEvents: (isLogoRestricted || croppedImage || currentLogoUrl) ? 'none' : 'auto',
                            }}
                            disabled={inputDisabled || isLogoRestricted}
                        />
                    </Box>

                    {(croppedImage || currentLogoUrl) && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage();
                            }}
                            sx={{
                                position: 'absolute',
                                top: -1,
                                right: -3,
                                color: '#828282',
                                backgroundColor: 'rgb(255, 255, 255)',
                                '&:hover': { backgroundColor: 'rgb(255, 255, 255)' },
                                width: 26,
                                height: 26,
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                zIndex: 2,
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                    )}
                </Box>

                <Typography
                    variant="body2"
                    sx={{
                        flex: '1',
                        fontSize: '0.8rem',
                        marginLeft: '10px',

                        maxWidth: '50%',

                        color: '#4f4f4f',
                        display: 'flex', // Add display flex
                        alignItems: 'center', // Center text vertically
                    }}



                >
                    Add custom logo to be displayed in the middle of the QR code
                </Typography>

            </Box>

            <Modal
                open={open}
                onClose={handleClose}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backdropFilter: 'blur(7px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <Box
                    sx={{
                        outline: 'none',
                        width: '90vw',
                        maxWidth: '900px',
                        height: '90vh',
                        maxHeight: '700px',
                    }}
                >
                    {image && (
                        <>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 'calc(100% - 100px)',
                                    marginBottom: 2,
                                }}
                            >
                                <Cropper
                                    image={image}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    cropShape="rect"
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                    onMediaLoaded={onMediaLoaded}
                                    objectFit="contain"
                                    centerImage={true}
                                    showGrid={false}
                                    minZoom={0.1}
                                    maxZoom={3}
                                    restrictPosition={false}
                                />
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Slider
                                    value={zoom}
                                    onChange={(e, newValue) => setZoom(newValue)}
                                    min={0.1}
                                    max={3}
                                    step={0.01}
                                    valueLabelDisplay="auto"
                                    defaultValue={1}
                                    sx={{
                                        marginBottom: 2,
                                        width: '80%',
                                        margin: '0 auto 20px auto'
                                    }}
                                />




                                <div>
                                    <Button onClick={handleClose} variant="contained" sx={{ marginRight: 2 }}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleSave} variant="contained" color="primary">
                                        Crop
                                    </Button>
                                </div>


                            </Box>


                        </>
                    )}
                </Box>


            </Modal>
        </Box>
    );
};

export default TeamQrCodeCustomLogo;